import React, { useMemo, useState } from 'react';
import { Button, Popover, List } from 'antd';
import { ExperimentOutlined } from '@ant-design/icons';

export interface SkillStudioAddToEvalMessage {
    type: string;
    run_id?: string;
    thread_id?: string;
    user_input?: string;
    question?: string;
}

export interface SkillStudioAddToEvalButtonProps {
    messages: SkillStudioAddToEvalMessage[];
    activeThreadId?: string;
    loading?: boolean;
    onSelectMessage: (message: SkillStudioAddToEvalMessage) => void | Promise<void>;
    groundTruthForm: React.ReactNode;
}

const SkillStudioAddToEvalButton: React.FC<SkillStudioAddToEvalButtonProps> = ({
    messages,
    activeThreadId,
    loading = false,
    onSelectMessage,
    groundTruthForm,
}) => {
    const [popoverOpen, setPopoverOpen] = useState(false);

    const aiMessages = useMemo(
        () => messages.filter((m) => m.type === 'ai' && m.run_id && m.thread_id),
        [messages],
    );

    if (!activeThreadId || aiMessages.length === 0) return null;

    const truncate = (s: string | undefined, len = 80) =>
        s && s.length > len ? `${s.slice(0, len)}...` : s || '(no question)';

    const content = (
        <List
            size="small"
            style={{ maxHeight: 300, overflow: 'auto', width: 340 }}
            dataSource={aiMessages}
            renderItem={(msg) => (
                <List.Item
                    style={{ cursor: 'pointer', padding: '6px 8px' }}
                    onClick={() => {
                        setPopoverOpen(false);
                        void onSelectMessage(msg);
                    }}
                >
                    {truncate(msg.user_input ?? msg.question)}
                </List.Item>
            )}
        />
    );

    return (
        <>
            <Popover
                title="Select a response to add to Eval Set"
                content={content}
                trigger="click"
                open={popoverOpen}
                onOpenChange={setPopoverOpen}
                placement="topRight"
            >
                <Button
                    type="primary"
                    shape="circle"
                    size="large"
                    icon={<ExperimentOutlined />}
                    loading={loading}
                    style={{
                        position: 'fixed',
                        bottom: 80,
                        right: 24,
                        zIndex: 1000,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    }}
                    title="Add to Eval Set"
                />
            </Popover>
            {groundTruthForm}
        </>
    );
};

export default SkillStudioAddToEvalButton;
